import { useState, useEffect, useMemo, useRef } from 'react';
import './styles.css';
import { getLayout6ThemeStyle } from './theme';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Menu, Phone, MessageCircle, MapPin, Clock, Video, CreditCard, Shield, Car, ShoppingCart, FileText, Wrench, Bookmark, Search, Users, Package, Settings, Gauge, Handshake, HandCoins, X, Navigation, ChevronDown, Layout } from 'lucide-react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Link } from 'react-router-dom';
import { ConfigModal } from '@/components/ConfigModal';
import { ServiceModal } from '@/components/ServiceModal';
import { StoreSelectionModal } from '@/components/StoreSelectionModal';
import { LayoutSelectionModal } from '@/components/LayoutSelectionModal';
import { AccountIdModal } from '@/components/AccountIdModal';
import { useAppData } from '@/contexts/AppDataContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useStore } from '@/contexts/StoreContext';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { openWhatsApp } from '@/lib/whatsapp';
import { getAccountId } from '@/lib/obfuscate';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { requestUserLocation, getDistanceToStore, formatDistance } from '@/lib/geolocation';
import { useIsMobile } from '@/hooks/use-mobile';
import { HeaderBrandLogo } from '@/components/HeaderBrandLogo';
import {
  SERVICE_CATALOG,
  getServiceButtonText,
  getServiceInfo,
} from './serviceCatalog';

interface HeaderProps {
  config?: any;
}

export const Header = ({ config: propConfig }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isServicesModalOpen, setIsServicesModalOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isLayoutModalOpen, setIsLayoutModalOpen] = useState(false);
  const [isAccountIdModalOpen, setIsAccountIdModalOpen] = useState(false);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const { config, vehicles, accountId } = useAppData();
  const { colors } = useTheme();
  const { selectedStoreIndex, setSelectedStoreIndex, availableStores, selectedStore } = useStore();

  // Get original account ID from environment (for button visibility check)
  const originalAccountId = getAccountId();
  const shouldShowDevButtons = originalAccountId === '677e7e1b763f79265b9228db';

  // Use context config if no prop config is provided
  const currentConfig = propConfig || config;

  // Use selected store from context
  const store = selectedStore || currentConfig.stores[0];

  // Check if should show store selector (more than 1 available store)
  const showStoreSelector = availableStores.length > 1;

  // Update favorites count - only count vehicles that still exist
  useEffect(() => {
    const updateFavoritesCount = async () => {
      try {
        const favoriteIds = await secureGetItem('favorites');

        if (favoriteIds && vehicles.length > 0 && Array.isArray(favoriteIds)) {
          // Filter to only count favorites that still exist in the vehicle list
          const existingFavorites = favoriteIds.filter(id =>
            vehicles.some(v => v.id === id)
          );

          setFavoritesCount(existingFavorites.length);

          // Clean up localStorage if there are sold vehicles
          if (existingFavorites.length !== favoriteIds.length) {
            await secureSetItem('favorites', existingFavorites);
          }
        } else {
          setFavoritesCount(0);
        }
      } catch (error) {
        setFavoritesCount(0);
      }
    };

    // Initial count
    updateFavoritesCount();

    // Listen for favorites updates
    window.addEventListener('favoritesUpdated', updateFavoritesCount);
    window.addEventListener('storage', updateFavoritesCount);

    return () => {
      window.removeEventListener('favoritesUpdated', updateFavoritesCount);
      window.removeEventListener('storage', updateFavoritesCount);
    };
  }, [vehicles]);

  const headerColor = colors.header;
  const menuColor = colors.menu;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const buttonColor = colors.button;
  const textColor = colors.text;

  // Format opening hours for display in header - groups consecutive days with same hours
  const getFormattedHours = () => {
    const hours = store?.openingHours?.default;
    if (!hours) return 'Seg-Sex: 8h-19h | Sáb: 8h-18h';

    const daysOrder = [
      { key: 'monday', short: 'Seg' },
      { key: 'tuesday', short: 'Ter' },
      { key: 'wednesday', short: 'Qua' },
      { key: 'thursday', short: 'Qui' },
      { key: 'friday', short: 'Sex' },
      { key: 'saturday', short: 'Sáb' },
      { key: 'sunday', short: 'Dom' }
    ];

    // Group consecutive days with same hours
    const groups: Array<{ days: string[]; time: string }> = [];
    let currentGroup: { days: string[]; time: string } | null = null;

    daysOrder.forEach((day) => {
      const dayHours = hours[day.key];
      const timeStr = dayHours?.from && dayHours?.to
        ? `${dayHours.from}-${dayHours.to}`
        : 'Fechado';

      if (!currentGroup) {
        // Start new group
        currentGroup = { days: [day.short], time: timeStr };
      } else if (currentGroup.time === timeStr) {
        // Same hours - add to current group
        currentGroup.days.push(day.short);
      } else {
        // Different hours - save current group and start new one
        groups.push(currentGroup);
        currentGroup = { days: [day.short], time: timeStr };
      }
    });

    // Add last group
    if (currentGroup) {
      groups.push(currentGroup);
    }

    // Format groups for display (limit to 3 groups for header space)
    const formattedGroups = groups.slice(0, 3).map(group => {
      const dayRange = group.days.length > 1
        ? `${group.days[0]} à ${group.days[group.days.length - 1]}`
        : group.days[0];
      return `${dayRange}: ${group.time}`;
    });

    if (formattedGroups.length === 0) {
      return 'Consulte horários';
    }

    return formattedGroups.join(' | ');
  };

  const formattedHours = getFormattedHours();

  // Request user location only when needed (user interaction)
  // Don't request automatically on page load to avoid trust issues

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);
  const isMobile = useIsMobile();
  const hasWebBanners = store.banners?.filter((v: any) => v.platform === (isMobile ? 'mobile' : 'web')).length > 0;

  const mainServiceInfo = mainServiceId ? getServiceInfo(mainServiceId) : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';

  // Get service image (from config or default)
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const menuItems = [
    { label: 'Início', href: '/', isLink: true },
    { label: 'Estoque', href: '/estoque', isLink: true },
    { label: 'Serviços', href: '/servicos', isLink: true },
    { label: 'Contato', href: '/contato', isLink: true }
  ];

  const logoSrc =
    currentConfig.logo?.dark ||
    currentConfig.logo?.light ||
    currentConfig.icon?.dark ||
    currentConfig.icon?.light;

  const handleStoreChange = (index: number) => {
    setSelectedStoreIndex(index);
  };

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const syncHeaderHeight = () => {
      const height = `${el.offsetHeight}px`;
      document.documentElement.style.setProperty('--wl6-header-h', height);
      el.parentElement?.style.setProperty('--wl6-header-h', height);
    };

    syncHeaderHeight();
    const observer = new ResizeObserver(syncHeaderHeight);
    observer.observe(el);
    window.addEventListener('resize', syncHeaderHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', syncHeaderHeight);
    };
  }, [showStoreSelector, formattedHours, store.company]);

  const storeDistances = useMemo(() => {
    if (!userLocation) return {} as Record<string, number>;
    const distances: Record<string, number> = {};
    availableStores.forEach((storeOption: any) => {
      if (storeOption.place?.latitude && storeOption.place?.longitude) {
        distances[storeOption._id] = getDistanceToStore(
          userLocation.lat,
          userLocation.lng,
          storeOption.place.latitude,
          storeOption.place.longitude
        );
      }
    });
    return distances;
  }, [userLocation, availableStores]);

  return (
    <header
      ref={headerRef}
      className="layout-6 fixed top-0 left-0 right-0 z-50"
      style={getLayout6ThemeStyle(colors)}
    >
      {/* Topbar compacta — desktop */}
      <div className="wl6-topbar hidden lg:block" style={{ backgroundColor: menuColor, color: headerColor }}>
        <div className="wl6-container flex items-center justify-between h-9">
          <div className="flex items-center gap-2">
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{formattedHours}</span>
          </div>
          <div className="flex items-center gap-5">
            {showStoreSelector && (
              <button onClick={() => setIsStoreModalOpen(true)} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
                <MapPin className="h-3.5 w-3.5" />
                <span className="truncate max-w-[180px]">{store.company || `Loja ${selectedStoreIndex + 1}`}</span>
                <ChevronDown className="h-3 w-3" />
              </button>
            )}
            <a href={store.phone?.ios} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
              <Phone className="h-3.5 w-3.5" />
              <span>{store.phone?.formatted}</span>
            </a>
            <button onClick={(e) => { e.preventDefault(); openWhatsApp(store.whatsapp?.ios); }} className="flex items-center gap-1.5 hover:opacity-80 transition-opacity">
              <MessageCircle className="h-3.5 w-3.5" />
              <span>WhatsApp</span>
            </button>
          </div>
        </div>
      </div>

      <div className="wl6-header-bar is-scrolled">
      <div className="wl6-container">
        <div className="wl6-header-inner flex items-center h-14 lg:h-[60px] gap-3">
          <Link to="/" className="flex items-center flex-shrink-0 gap-2.5 min-w-0">
            {logoSrc && (
              <div className="wl6-logo-safe">
                <HeaderBrandLogo src={logoSrc} alt={currentConfig.name} variant="nav" />
              </div>
            )}
            {!currentConfig.logo?.light && !currentConfig.logo?.dark && (
              <div className="hidden sm:block min-w-0">
                <span className="wl6-header-brand-name font-bold text-sm block leading-tight truncate">{currentConfig.name}</span>
                {currentConfig.slogan && <span className="wl6-header-brand-name text-xs truncate block opacity-80">{currentConfig.slogan}</span>}
              </div>
            )}
          </Link>

          <div className="hidden lg:flex items-center gap-6 ml-auto flex-shrink-0">
            <nav className="flex items-center gap-0.5" aria-label="Menu principal">
              {menuItems.map((item) => (
                item.isLink ? (
                  <Link key={item.label} to={item.href} className="wl6-header-nav-link px-3.5 py-2 rounded-lg text-sm font-medium hover:opacity-80 transition-opacity">
                    {item.label}
                  </Link>
                ) : (
                  <a key={item.label} href={item.href} className="wl6-header-nav-link px-3.5 py-2 rounded-lg text-sm font-medium hover:opacity-80 transition-opacity">
                    {item.label}
                  </a>
                )
              ))}
            </nav>

            <div className="flex items-center gap-2">
            <Link to="/favoritos" className="relative">
              <Button variant="ghost" size="icon" className="wl6-header-icon-btn rounded-lg" aria-label="Ver favoritos">
                <Bookmark className="h-5 w-5" />
                {favoritesCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold text-white rounded-full px-1" style={{ backgroundColor: buttonColor }}>
                    {favoritesCount > 99 ? '99+' : favoritesCount}
                  </span>
                )}
              </Button>
            </Link>

            {hasWebBanners && mainService ? (
              <>
                <HoverCard open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen} openDelay={200}>
                  <HoverCardTrigger asChild>
                    <Button className="wl6-header-service-btn rounded-lg font-semibold px-4 h-9">
                      <MainServiceIcon className="h-4 w-4 mr-2" />
                      {mainServiceTitle}
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-[320px] p-0 overflow-hidden border-0 shadow-2xl rounded-2xl" side="bottom" align="end">
                    {mainServiceInfo && (
                      <div>
                        <div className="relative h-32 overflow-hidden">
                          <img src={mainServiceImage} alt={mainServiceInfo.title} className="w-full h-full object-cover" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                          <div className="absolute bottom-3 left-3 right-3">
                            <h3 className="text-white font-bold text-sm">{mainServiceInfo.title}</h3>
                          </div>
                        </div>
                        <div className="p-4 space-y-3">
                          <p className="text-xs text-muted-foreground">{mainServiceInfo.description}</p>
                          <Button
                            onClick={() => { setIsHoverCardOpen(false); setIsServiceModalOpen(true); }}
                            className="w-full text-white font-semibold rounded-xl"
                            style={{ backgroundColor: buttonColor }}
                          >
                            {getServiceButtonText(mainServiceId)}
                          </Button>
                        </div>
                      </div>
                    )}
                  </HoverCardContent>
                </HoverCard>
                <ServiceModal service={mainService} config={currentConfig} isOpen={isServiceModalOpen} onOpenChange={setIsServiceModalOpen} />
              </>
            ) : (
              <Button className="wl6-header-service-btn rounded-lg font-semibold px-4 h-9" onClick={(e) => { e.preventDefault(); openWhatsApp(store.whatsapp?.ios); }}>
                <MessageCircle className="h-4 w-4 mr-2" />
                Contato
              </Button>
            )}

            {shouldShowDevButtons && (
              <>
                <Button variant="ghost" size="icon" className="wl6-header-icon-btn rounded-lg" onClick={() => setIsLayoutModalOpen(true)} aria-label="Selecionar layout">
                  <Layout className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="icon" className="wl6-header-icon-btn rounded-lg" onClick={() => setIsAccountIdModalOpen(true)} aria-label="Alterar ID da conta">
                  <Settings className="h-5 w-5" />
                </Button>
              </>
            )}
            </div>
          </div>

          <div className="flex lg:hidden items-center gap-0.5 ml-auto flex-shrink-0">
            <a href={store.phone?.ios} className="wl6-header-mobile-action p-2 rounded-lg hover:opacity-80 transition-opacity" aria-label="Ligar">
              <Phone className="h-5 w-5" />
            </a>
            <button onClick={(e) => { e.preventDefault(); openWhatsApp(store.whatsapp?.ios); }} className="wl6-header-mobile-action p-2 rounded-lg hover:opacity-80 transition-opacity" aria-label="WhatsApp">
              <MessageCircle className="h-5 w-5" />
            </button>
            {showStoreSelector && (
              <button onClick={() => setIsStoreModalOpen(true)} className="wl6-header-mobile-action p-2 rounded-lg hover:opacity-80 transition-opacity" aria-label="Selecionar loja">
                <MapPin className="h-5 w-5" />
              </button>
            )}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="wl6-header-icon-btn rounded-lg" aria-label="Abrir menu">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md p-0 border-0 bg-white [&>button]:hidden" style={getLayout6ThemeStyle(colors)}>
                <div className="flex flex-col h-full wl6-mobile-menu">
                  <SheetHeader className="wl6-mobile-menu__header px-5 py-5 text-left space-y-0">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        {logoSrc && (
                          <div className="wl6-logo-safe p-1 shrink-0">
                            <HeaderBrandLogo src={logoSrc} alt={currentConfig.name} variant="sheet" />
                          </div>
                        )}
                        <div className="text-left min-w-0">
                          <SheetTitle className="wl6-mobile-menu__title text-base font-bold">{currentConfig.name}</SheetTitle>
                          {currentConfig.slogan && <p className="wl6-mobile-menu__subtitle text-xs mt-0.5 truncate">{currentConfig.slogan}</p>}
                        </div>
                      </div>
                      <button type="button" onClick={() => setIsOpen(false)} className="wl6-mobile-menu__close p-2 rounded-lg shrink-0" aria-label="Fechar menu">
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </SheetHeader>

                  <div className="flex-1 overflow-y-auto py-4 px-3">
                    <nav className="flex flex-col gap-0.5">
                      {menuItems.map((item: any) => (
                        item.isLink ? (
                          <Link key={item.label} to={item.href} onClick={() => setIsOpen(false)} className="wl6-mobile-menu__link">
                            {item.label}
                          </Link>
                        ) : (
                          <a key={item.label} href={item.href} onClick={() => setIsOpen(false)} className="wl6-mobile-menu__link">
                            {item.label}
                          </a>
                        )
                      ))}
                    </nav>

                    <Separator className="my-4" />

                    <Link to="/favoritos" onClick={() => setIsOpen(false)} className="wl6-mobile-menu__item">
                      <Bookmark className="h-5 w-5" />
                      <span>Favoritos</span>
                      {favoritesCount > 0 && (
                        <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: buttonColor }}>{favoritesCount}</span>
                      )}
                    </Link>

                    {showStoreSelector && (
                      <>
                        <Separator className="my-4" />
                        <p className="wl6-mobile-menu__label mb-2">Lojas</p>
                        <div className="space-y-0.5">
                          {availableStores.map((storeOption: any, index: number) => {
                            const distance = storeDistances[storeOption._id] || null;
                            return (
                              <button
                                key={storeOption._id}
                                type="button"
                                onClick={() => { handleStoreChange(index); setIsOpen(false); }}
                                className={`wl6-mobile-menu__store ${selectedStoreIndex === index ? 'is-active' : ''}`}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <MapPin className="h-4 w-4 flex-shrink-0 wl6-mobile-menu__store-meta" />
                                    <div className="min-w-0">
                                      <span className="text-sm block truncate">{storeOption.company}</span>
                                      {distance !== null && (
                                        <span className="wl6-mobile-menu__store-meta flex items-center gap-1 mt-0.5">
                                          <Navigation className="h-3 w-3" />
                                          {formatDistance(distance)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {selectedStoreIndex === index && <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: buttonColor }} />}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </>
                    )}
                  </div>

                  {hasWebBanners && mainService && (
                    <div className="wl6-mobile-menu__footer p-4">
                      <ServiceModal service={mainService} config={currentConfig}>
                        <Button className="wl6-mobile-menu__cta">
                          <MainServiceIcon className="h-5 w-5 mr-2" />
                          {mainServiceTitle}
                        </Button>
                      </ServiceModal>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      </div>

      {/* Store Selection Modal */}
      {showStoreSelector && (
        <StoreSelectionModal
          config={currentConfig}
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      )}

      {/* Layout Selection Modal */}
      {shouldShowDevButtons && (
        <LayoutSelectionModal
          isOpen={isLayoutModalOpen}
          onOpenChange={setIsLayoutModalOpen}
        />
      )}

      {/* Account ID Modal */}
      {shouldShowDevButtons && (
        <AccountIdModal
          isOpen={isAccountIdModalOpen}
          onOpenChange={setIsAccountIdModalOpen}
        />
      )}

      {/* Services Modal */}
      <Dialog open={isServicesModalOpen} onOpenChange={setIsServicesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 flex flex-col gap-0">
          {/* Fixed Header */}
          <div className="sticky top-0 z-10 bg-background border-b px-6 py-4">
            <h2 className="text-2xl font-bold text-foreground">Nossos Serviços</h2>
            <p className="text-muted-foreground mt-1">Conheça todos os serviços que oferecemos</p>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {store.services?.map((service: any) => {
                const info = SERVICE_CATALOG[service.service];
                if (!info) return null;

                const ServiceIcon = info.icon;

                return (
                  <ServiceModal key={service.service} service={service} config={currentConfig}>
                    <button className="w-full text-left group">
                      <div className="border border-border/50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-primary/50">
                        {/* Service Image */}
                        <div className="relative h-32 overflow-hidden bg-gradient-to-br from-muted to-muted/50">
                          <img
                            src={service.image || info.image}
                            alt={info.title}
                            className="w-full h-full object-cover transition-transform duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute top-2 right-2">
                            <div className={`w-10 h-10 ${info.color} rounded-lg flex items-center justify-center backdrop-blur-sm bg-white/10`}>
                              <ServiceIcon className="h-5 w-5 text-white" />
                            </div>
                          </div>
                        </div>

                        {/* Service Info */}
                        <div className="p-4">
                          <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                            {info.title}
                          </h3>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {info.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  </ServiceModal>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </header>
  );
};
